/**
 * Serviço do Primeiro Uso — cria modelo, funções, execução e tarefas
 * em uma única operação a partir de um template selecionado.
 */

import { supabase } from '../../../lib/supabaseClient';
import {
  createManualBlueprint,
  publishBlueprint,
  createWorkflowInstanceFromBlueprint,
} from '../../excelencia/services/excelenciaService';
import {
  ensureWorkflowRole,
} from '../../shared/services/workflowRoleService';
import type { ProcessTemplate } from '../data/processTemplates';

const ONBOARDING_KEY = 'wf_first_use_completed';

export interface RoleAssignment {
  role: string;
  userId: string;
  userName?: string;
}

export interface FirstUseResult {
  blueprintId: string;
  instanceId: string;
  tasksCreated: number;
  referenceCode: string;
}

/**
 * Verifica se a organização já completou o primeiro uso.
 */
export async function hasCompletedFirstUse(organizationId: string): Promise<boolean> {
  // Check localStorage first for fast response
  const localKey = `${ONBOARDING_KEY}_${organizationId}`;
  if (localStorage.getItem(localKey) === 'true') return true;

  // Check if org has any workflow instances
  const { count, error } = await supabase
    .from('workflow_instances_v9')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .limit(1);

  if (error) return false;

  const completed = (count ?? 0) > 0;
  if (completed) localStorage.setItem(localKey, 'true');
  return completed;
}

/**
 * Marca organização como tendo completado o primeiro uso.
 */
export function markFirstUseCompleted(organizationId: string): void {
  localStorage.setItem(`${ONBOARDING_KEY}_${organizationId}`, 'true');
}

/**
 * Executa todo o fluxo de primeiro uso:
 * 1. Cria blueprint a partir do template
 * 2. Publica o blueprint
 * 3. Garante que roles existam + vincula usuários
 * 4. Cria a primeira execução (instância + tasks)
 */
export async function executeFirstUse(params: {
  template: ProcessTemplate;
  organizationId: string;
  userId: string;
  title: string;
  roleAssignments: RoleAssignment[];
}): Promise<FirstUseResult> {
  const { template, organizationId, userId, title, roleAssignments } = params;

  // 1. Criar blueprint
  const blueprint = await createManualBlueprint({
    organizationId,
    name: template.name,
    description: template.description,
    category: 'processo',
    stages: template.stages.map((stage, index) => ({
      order: index + 1,
      name: stage.name,
      description: stage.description,
      slaHours: stage.slaHours,
      role: stage.role,
      rules: {
        canSkip: stage.canSkip,
        requireApproval: stage.requireApproval,
      },
    })),
    createdBy: userId,
    actorRole: 'admin',
  });

  // 2. Publicar blueprint
  await publishBlueprint(blueprint.id, userId, 'admin');

  // 3. Garantir roles e vincular usuários
  for (const assignment of roleAssignments) {
    const roleRecord = await ensureWorkflowRole(organizationId, assignment.role);
    if (!roleRecord) continue;

    // Vincular usuário ao role (ignora conflito se já existe)
    await supabase
      .from('user_roles')
      .upsert(
        {
          organization_id: organizationId,
          user_id: assignment.userId,
          role_id: roleRecord.id,
        },
        { onConflict: 'user_id,role_id' },
      );
  }

  // 4. Criar execução
  const result = await createWorkflowInstanceFromBlueprint({
    blueprintId: blueprint.id,
    organizationId,
    title,
    priority: 'media',
    initiatedBy: userId,
  });

  // 5. Marcar primeiro uso como completo
  markFirstUseCompleted(organizationId);

  return {
    blueprintId: blueprint.id,
    instanceId: result.instanceId,
    tasksCreated: result.tasksCreated,
    referenceCode: result.referenceCode,
  };
}

/**
 * Lista membros da organização para seleção no wizard.
 */
export async function listOrgMembers(organizationId: string): Promise<Array<{
  userId: string;
  email: string;
  name: string;
  role: string;
}>> {
  const { data: memberships, error: memErr } = await supabase
    .from('user_organizations')
    .select('user_id, role')
    .eq('organization_id', organizationId);

  if (memErr) throw memErr;
  if (!memberships || memberships.length === 0) return [];

  const userIds = memberships.map((m) => m.user_id);
  const { data: profiles, error: profErr } = await supabase
    .from('user_profiles')
    .select('user_id, email, full_name')
    .in('user_id', userIds);

  if (profErr) throw profErr;

  const profileMap = new Map(
    (profiles ?? []).map((p) => [p.user_id, p]),
  );

  return memberships.map((m) => {
    const profile = profileMap.get(m.user_id);
    return {
      userId: m.user_id,
      email: profile?.email ?? '',
      name: profile?.full_name ?? profile?.email ?? 'Usuário',
      role: m.role ?? 'operator',
    };
  });
}
